import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, asArray } from "@/lib/api";
import { Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";

const emptyDraft = { id: null, name: "", body: "" };

/**
 * Modal to manage the pool of WhatsApp message templates.
 * Templates support the variables: {nome}, {primeiro_nome}, {localidade},
 * {email}, {telefone} — interpolated when applied to a specific lead.
 */
export const WhatsAppTemplatesManager = ({ open, onOpenChange, onChanged }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(emptyDraft);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/whatsapp-templates");
            setItems(asArray(data));
        } catch (err) {
            toast.error("Erro a carregar templates.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            load();
            setEditing(emptyDraft);
        }
    }, [open]);

    const save = async () => {
        const name = editing.name.trim();
        const body = editing.body.trim();
        if (!name || !body) {
            toast.error("Preencha o nome e a mensagem.");
            return;
        }
        setSaving(true);
        try {
            if (editing.id) {
                const { data } = await api.patch(
                    `/admin/whatsapp-templates/${editing.id}`, { name, body },
                );
                setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
                toast.success("Template atualizado.");
            } else {
                const { data } = await api.post(
                    "/admin/whatsapp-templates", { name, body },
                );
                setItems((arr) => [...arr, data]);
                toast.success("Template criado.");
            }
            setEditing(emptyDraft);
            onChanged?.();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error(`Erro a guardar: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Apagar este template?")) return;
        try {
            await api.delete(`/admin/whatsapp-templates/${id}`);
            setItems((arr) => arr.filter((x) => x.id !== id));
            toast.success("Template removido.");
            onChanged?.();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error(`Erro: ${detail}`);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[85vh] overflow-y-auto"
                data-testid="wa-templates-manager"
            >
                <DialogHeader>
                    <DialogTitle>Templates de WhatsApp</DialogTitle>
                    <DialogDescription>
                        Mensagens pré-definidas para responder a leads mais depressa. Podes usar as variáveis{" "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{primeiro_nome}"}</code>{", "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{nome}"}</code>{", "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{localidade}"}</code>{", "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{email}"}</code>{", "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{telefone}"}</code> e{" "}
                        <code className="bg-stone-100 px-1 rounded text-xs">{"{link_proposta}"}</code>
                        {" "}(substituído pelo link do PDF selecionado ao enviar).
                    </DialogDescription>
                </DialogHeader>

                {/* Editor */}
                <div className="bg-stone-50 ring-1 ring-stone-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        {editing.id ? (
                            <Pencil size={16} className="text-stone-500" />
                        ) : (
                            <Plus size={16} className="text-stone-500" />
                        )}
                        <span className="text-sm font-medium text-stone-700">
                            {editing.id ? "Editar template" : "Novo template"}
                        </span>
                        {editing.id && (
                            <button
                                type="button"
                                onClick={() => setEditing(emptyDraft)}
                                className="ml-auto text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2"
                            >
                                Cancelar edição
                            </button>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Nome</Label>
                        <Input
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            placeholder="ex: Follow-up 3 dias"
                            className="mt-1.5 h-10"
                            data-testid="wa-tpl-name"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Mensagem</Label>
                        <Textarea
                            value={editing.body}
                            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                            placeholder="Olá {primeiro_nome}! ..."
                            className="mt-1.5 min-h-[120px]"
                            data-testid="wa-tpl-body"
                        />
                    </div>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 h-10"
                        data-testid="wa-tpl-save"
                    >
                        {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                        {editing.id ? "Guardar alterações" : "Criar template"}
                    </Button>
                </div>

                {/* List */}
                <div className="mt-4">
                    <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">
                        Templates existentes {items.length > 0 && `(${items.length})`}
                    </Label>
                    {loading ? (
                        <div className="text-sm text-stone-400 py-6 text-center">A carregar…</div>
                    ) : items.length === 0 ? (
                        <div className="text-sm text-stone-400 py-6 text-center">
                            Sem templates criados.
                        </div>
                    ) : (
                        <ul className="mt-2 space-y-2 max-h-[300px] overflow-y-auto">
                            {items.map((t) => (
                                <li
                                    key={t.id}
                                    className="px-4 py-3 bg-white ring-1 ring-stone-200 rounded-lg"
                                    data-testid={`wa-tpl-item-${t.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="font-medium text-stone-800">{t.name}</span>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8"
                                                onClick={() => setEditing({ id: t.id, name: t.name, body: t.body })}
                                                data-testid={`wa-tpl-edit-${t.id}`}
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8 hover:bg-rose-50 hover:text-rose-600"
                                                onClick={() => remove(t.id)}
                                                data-testid={`wa-tpl-delete-${t.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-stone-500 whitespace-pre-wrap line-clamp-3">
                                        {t.body}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        <X size={16} className="mr-2" /> Fechar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WhatsAppTemplatesManager;
